/* One front door for every file the team drops in.
 *
 * Detection reads the file, not the extension, because a plan saved as XML and
 * a plan renamed to XML look identical from the outside and only one of them
 * works. Where a file cannot be handled the message says what to do about it
 * rather than what went wrong.
 */
import { parseMsProjectXml, looksLikeMsProjectXml, inferWorkstreams } from "./msproject.js";
import { readWorkbook, readCsv, looksLikeWorkbook, looksLikeCsv } from "./spreadsheet.js";

export const SCHEDULE_ACCEPT = ".xml";

/* Formats we can read today, and the ones we cannot, with the reason. Kept
   here so the Import page and the error messages cannot disagree. */
export const FORMATS = [
  { label: "Excel workbook", ext: ".xlsx", ready: true,
    how: "Any sheet with a header row becomes a tracker with its own columns." },
  { label: "CSV export", ext: ".csv", ready: true,
    how: "What most client systems export. Read the same way as a sheet." },
  { label: "MS Project XML", ext: ".xml", ready: true,
    how: "In MS Project: File, then Save As, then choose XML." },
  { label: "MS Project .mpp", ext: ".mpp", ready: false,
    how: "The binary format needs a Java library that cannot run in a browser. " +
         "Save the plan as XML instead, which takes about ten seconds and costs nothing." },
  { label: "Older Excel", ext: ".xls", ready: false,
    how: "The pre-2007 binary format. Open it in Excel and save it as .xlsx." },
  { label: "Primavera P6", ext: ".xer or .xml", ready: false,
    how: "Send a real P6 export and this can be added. Writing it against the " +
         "documentation alone would be guesswork." },
];

/* What kind of file this is, decided before anything tries to read it. */
export function classify(file) {
  const name = (file.name || "").toLowerCase();
  if (name.endsWith(".mpp")) return "mpp";
  if (name.endsWith(".xls")) return "xls";
  if (looksLikeWorkbook(name)) return "workbook";
  if (looksLikeCsv(name)) return "csv";
  if (name.endsWith(".xml") || name.endsWith(".xer")) return "schedule";
  return "unknown";
}

export { readWorkbook, readCsv };

export async function readSchedule(file) {
  const name = (file.name || "").toLowerCase();

  if (name.endsWith(".mpp")) {
    throw new Error(
      "This is the binary .mpp format, which a browser cannot open. In MS " +
      "Project use File, then Save As, then choose XML, and drop that file " +
      "here instead. It takes about ten seconds and nothing else changes.",
    );
  }

  const content = await file.text();

  if (looksLikeMsProjectXml(content)) {
    const parsed = parseMsProjectXml(content);
    return { ...parsed, activities: inferWorkstreams(parsed.activities) };
  }

  if (/<APIBusinessObjects/i.test(content) || name.endsWith(".xer")) {
    throw new Error(
      "This looks like a Primavera P6 export. Nothing here reads P6 yet, " +
      "because writing it without a real export to test against would be " +
      "guesswork. Send one and it can be added.",
    );
  }

  throw new Error(
    `${file.name} is not a schedule this can read. It handles MS Project XML: ` +
    "File, then Save As, then XML.",
  );
}

/* What a commit is about to do, in the words a person would use. Separated
   from the writing so the preview and the confirmation cannot describe
   different things. */
export function describeCommit({ activities, mode, existing }) {
  const replacing = mode === "replace" && existing > 0;
  return [
    `${activities.length} activities`,
    replacing
      ? `replacing the ${existing} already here`
      : existing > 0
        ? `added to the ${existing} already here`
        : "into an empty schedule",
  ].join(", ");
}
